import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import BookingForm from "../components/BookingForm";
import { useI18n } from "../lib/i18n";
import { http } from "../lib/api";
import { useCompanySettings } from "../lib/useCompanySettings";
import { whatsappHref } from "../lib/phone";
import { Star, ShieldCheck, Banknote, Plane, Clock, CreditCard, Phone, MessageCircle, Mail, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";

const HERO_BG = "https://images.pexels.com/photos/20302282/pexels-photo-20302282.jpeg";
const CHAUFFEUR_IMG = "https://images.pexels.com/photos/15774577/pexels-photo-15774577.jpeg";

const ADV_ICONS = [ShieldCheck, Banknote, Plane, Clock, CreditCard];

export default function Home() {
  const { t } = useI18n();
  const s = useCompanySettings();
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    http.get("/reviews").then(({ data }) => setReviews(data)).catch(() => {});
  }, []);

  return (
    <div className="text-white">
      {/* Hero */}
      <section className="relative overflow-hidden hero-overlay" style={{ minHeight: "92vh" }}>
        <img src={HERO_BG} alt="Sam Taxi Netherlands"
          className="absolute inset-0 w-full h-full object-cover" />
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10 pt-20 lg:pt-28 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="fade-up">
            <div className="text-xs tracking-[0.4em] uppercase text-[#D4AF37] mb-5">{t("hero.eyebrow")}</div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
              {t("hero.title_a")} <br />
              <span className="text-gold-gradient italic">{t("hero.title_b")}</span>
            </h1>
            <p className="mt-6 text-white/75 text-lg max-w-xl leading-relaxed">{t("hero.subtitle")}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#book">
                <Button data-testid="hero-book-btn" className="h-12 px-6 bg-[#D4AF37] hover:bg-[#B5952F] text-[#040814] font-semibold">
                  {t("hero.cta_book")} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <a href={`tel:${s.phone}`}>
                <Button data-testid="hero-call-btn" variant="ghost" className="h-12 px-6 border border-white/25 text-white hover:bg-white/5">
                  <Phone className="w-4 h-4 mr-2" /> {t("hero.cta_call")}
                </Button>
              </a>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-white/55">
              <div className="flex gap-1 text-[#D4AF37]">{[0,1,2,3,4].map(i=><Star key={i} className="w-4 h-4 fill-[#D4AF37]"/>)}</div>
              <span>Rated 4.9 / 5 · 2,400+ rides</span>
            </div>
          </div>
          <div id="book" className="lg:justify-self-end w-full fade-up">
            <BookingForm />
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-28">
        <div className="text-center mb-16">
          <div className="text-xs tracking-[0.4em] uppercase text-[#D4AF37] mb-3">Sam Taxi</div>
          <h2 className="font-heading text-4xl lg:text-5xl">{t("adv.title")}</h2>
          <div className="gold-divider mt-6 mx-auto w-32" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {t("adv.items").map((it, i) => {
            const Icon = ADV_ICONS[i] || ShieldCheck;
            return (
              <div key={i} data-testid={`adv-${i}`} className="hover-glow rounded-xl border border-white/10 bg-[#0A1128] p-8">
                <div className="w-12 h-12 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center mb-5">
                  <Icon className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <h3 className="font-heading text-xl mb-2">{it[0]}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{it[1]}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Chauffeur split */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-20 grid lg:grid-cols-2 gap-12 items-center">
        <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-white/10">
          <img src={CHAUFFEUR_IMG} alt="Professional chauffeur" className="w-full h-full object-cover" />
        </div>
        <div>
          <div className="text-xs tracking-[0.4em] uppercase text-[#D4AF37] mb-3">Our Promise</div>
          <h2 className="font-heading text-4xl lg:text-5xl mb-6">Chauffeured by professionals</h2>
          <p className="text-white/70 text-lg leading-relaxed mb-4">
            Every Sam Taxi driver is uniformed, background-checked, and trained for premium customer service. Our fleet is meticulously maintained and equipped for comfort.
          </p>
          <ul className="space-y-3 text-white/70">
            <li className="flex gap-3"><span className="text-[#D4AF37]">—</span> Mercedes & premium sedans</li>
            <li className="flex gap-3"><span className="text-[#D4AF37]">—</span> Flight tracking for airport pickups</li>
            <li className="flex gap-3"><span className="text-[#D4AF37]">—</span> Child seats on request</li>
            <li className="flex gap-3"><span className="text-[#D4AF37]">—</span> Multilingual chauffeurs (NL, EN, AR)</li>
          </ul>
        </div>
      </section>

      {/* Airports */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs tracking-[0.4em] uppercase text-[#D4AF37] mb-3">Airport Transfers</div>
            <h2 className="font-heading text-4xl lg:text-5xl">Fixed-price luchthaven transfers</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            ["schiphol", "Schiphol", "AMS"],
            ["eindhoven", "Eindhoven", "EIN"],
            ["rotterdam", "Rotterdam", "RTM"],
            ["brussels", "Brussels", "BRU"],
            ["dusseldorf", "Düsseldorf", "DUS"],
          ].map(([slug, name, code]) => (
            <Link
              key={slug}
              to={`/airports/${slug}`}
              data-testid={`airport-card-${slug}`}
              className="group hover-glow rounded-xl border border-white/10 bg-[#0A1128] p-6 text-left"
            >
              <Plane className="w-6 h-6 text-[#D4AF37] mb-3" />
              <div className="text-xs tracking-[0.3em] uppercase text-white/40">{code}</div>
              <div className="font-heading text-2xl mt-1">{name}</div>
              <div className="mt-4 text-sm text-[#D4AF37] inline-flex items-center gap-1">
                Book transfer <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Reviews */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-28">
        <div className="text-center mb-12">
          <div className="text-xs tracking-[0.4em] uppercase text-[#D4AF37] mb-3">Testimonials</div>
          <h2 className="font-heading text-4xl lg:text-5xl">{t("reviews.title")}</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {reviews.map((r) => (
            <div key={r._id} data-testid={`review-${r._id}`} className="rounded-xl border border-white/10 bg-[#0A1128] p-8">
              <div className="flex gap-1 text-[#D4AF37] mb-4">
                {Array.from({ length: r.rating || 5 }).map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-[#D4AF37]" />
                ))}
              </div>
              <p className="text-white/80 leading-relaxed">&ldquo;{r.text}&rdquo;</p>
              <div className="mt-6 pt-4 border-t border-white/10">
                <div className="font-heading text-lg">{r.name}</div>
                <div className="text-xs text-white/40">{r.city}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-20" id="contact">
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <div className="text-xs tracking-[0.4em] uppercase text-[#D4AF37] mb-3">{t("contact.title")}</div>
            <h2 className="font-heading text-4xl lg:text-5xl mb-6">Get in touch</h2>
            <p className="text-white/70 max-w-md">Call us, message on WhatsApp or send an email — we reply within minutes, 24/7.</p>
            <div className="mt-8 space-y-3 text-white/85">
              <div className="flex items-center gap-3"><Phone className="w-4 h-4 text-[#D4AF37]" /> <a data-testid="contact-phone" href={`tel:${s.phone}`} className="hover:text-[#D4AF37]">{s.phone}</a></div>
              <div className="flex items-center gap-3"><MessageCircle className="w-4 h-4 text-[#D4AF37]" /> <a data-testid="contact-whatsapp" href={whatsappHref(s.whatsapp) || "#"} target="_blank" rel="noreferrer noopener" className="hover:text-[#D4AF37]">{s.whatsapp}</a></div>
              <div className="flex items-center gap-3"><Mail className="w-4 h-4 text-[#D4AF37]" /> <a data-testid="contact-email" href={`mailto:${s.email}`} className="hover:text-[#D4AF37]">{s.email}</a></div>
              <div>{s.address}</div>
            </div>
            <iframe
              title="Map"
              className="mt-8 w-full h-64 rounded-xl border border-white/10"
              src="https://www.openstreetmap.org/export/embed.html?bbox=4.7%2C52.3%2C5.0%2C52.4&amp;layer=mapnik"
            />
          </div>
          <form
            className="rounded-xl border border-white/10 bg-[#0A1128] p-8 space-y-4"
            onSubmit={(e) => { e.preventDefault(); }}
          >
            <input data-testid="contact-name" required placeholder={t("contact.form_name")} className="w-full bg-[#040814] border border-white/20 rounded-md px-4 py-3 text-white" />
            <input data-testid="contact-email" required type="email" placeholder={t("contact.form_email")} className="w-full bg-[#040814] border border-white/20 rounded-md px-4 py-3 text-white" />
            <textarea data-testid="contact-message" required rows={5} placeholder={t("contact.form_message")} className="w-full bg-[#040814] border border-white/20 rounded-md px-4 py-3 text-white" />
            <Button data-testid="contact-submit" type="submit" className="w-full h-12 bg-[#D4AF37] hover:bg-[#B5952F] text-[#040814] font-semibold">
              {t("contact.send")}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
